import React, { useEffect, useMemo } from "react";
import SvgMapper from "../../features/common/SvgMapper";

const ReviewModal = ({ isOpen, onClose, interview }) => {
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  const report = useMemo(() => {
    if (!interview?.student_feedback) {
      return {
        summary: "No feedback available for this interview.",
        strengths: [],
        areas_to_improve: [],
        actionable_next_steps: []
      };
    }

    try {
      const feedback = typeof interview.student_feedback === 'string' 
        ? JSON.parse(interview.student_feedback)
        : interview.student_feedback;

      return {
        summary: feedback.summary || "No summary available.",
        strengths: Array.isArray(feedback.strengths) ? feedback.strengths : [],
        areas_to_improve: Array.isArray(feedback.areas_to_improve) ? feedback.areas_to_improve : [],
        actionable_next_steps: Array.isArray(feedback.actionable_next_steps) 
          ? feedback.actionable_next_steps 
          : []
      };
    } catch (error) {
      console.error("Error parsing student_feedback:", error);
      return {
        summary: "Error parsing feedback data.",
        strengths: [],
        areas_to_improve: [],
        actionable_next_steps: []
      };
    }
  }, [interview]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Interview Review</h2>
            {interview && (
              <p className="text-sm text-gray-600 mt-1">
                {interview.interview_name} • {interview.category}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            {SvgMapper.closeIcon("w-6 h-6")}
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Summary</h3>
            <p className="text-gray-700 leading-relaxed">{report.summary}</p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <span className="text-green-600 mr-2">✓</span>
              Strengths
            </h3>
            <ul className="space-y-2">
              {report.strengths.map((strength, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-green-600 mr-2 mt-1">•</span>
                  <span className="text-gray-700">{strength}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <span className="text-orange-600 mr-2">⚠</span>
              Areas for Improvement
            </h3>
            <ul className="space-y-2">
              {report.areas_to_improve.map((weakness, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-orange-600 mr-2 mt-1">•</span>
                  <span className="text-gray-700">{weakness}</span>
                </li>
              ))}
            </ul>
          </div>

          {report.actionable_next_steps && report.actionable_next_steps.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <span className="text-blue-600 mr-2">→</span>
                Actionable Next Steps
              </h3>
              <ul className="space-y-2">
                {report.actionable_next_steps.map((step, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-blue-600 mr-2 mt-1">•</span>
                    <span className="text-gray-700">{step}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!interview?.student_feedback && (
            <div className="text-center py-8 text-gray-500">
              <p>No feedback available for this interview yet.</p>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewModal;

